import React, { useState, useEffect } from "react";
import "./PartnershipForm.css";
import solutionIcon from "../../../assets/ambition_icons/solutions-lab-icon.svg";
import "../../Ambition/components/AmbitionIntro/index.css";
import { getTransportCountryData } from "../../../apis/Dropdown.js";
import selectRemove from "../../../assets/partnership_icons/select-remove.svg";
import partnershipService from "../../../apis/Partnerships/partnershipService.js";

const PartnershipForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    company_name: "",
    company_description: "",
    general_tagline: "",
    yfs_discount: false,
    yfs_discount_percentage: "",
    free_shipping: false,
    shipping_countries: [],
    social_impact: "",
    lca_impact: "",
    business_model: "",
    availability: "",
    business_size: "",
    marketing_contact_person: "",
    marketing_contact_phone: "",
    marketing_contact_email: "",
    contact_person: "",
    contact_phone: "",
    contact_email: "",
    same_contact: false, // New field to track if contacts are the same
    // Business details
    company_address_line1: "",
    company_address_line2: "",
    postal_code: "",
    city: "",
    country: "",
    vat_number: "",
    company_email: "", // <-- Add this
    company_phone_number: "", // <-- Add this
    // Label
    label: "",
  });

  const [featuredProducts, setFeaturedProducts] = useState([
    {
      product_name: "",
      product_description: "",
      image: null,
      price: "",
      unit_qty: "",
      unit: "",
      individual_store_price: false,
      // New fields:
      lca_calculation: null,
      co2e: "",
    },
  ]);

  const [documents, setDocuments] = useState({
    supplier_report: null,
    lab_test: null,
    certification: null,
    reference: null,
    company_logo: null,
    certificate_logo: null,
  });

  // New state for document links and link texts
  const [documentLinks, setDocumentLinks] = useState({
    supplier_report: "",
    lab_test: "",
    certification: "",
    reference: "",
    company_logo: "",
    certificate_logo: "",
  });

  const [documentLinkTexts, setDocumentLinkTexts] = useState({
    supplier_report: "",
    lab_test: "",
    certification: "",
    reference: "",
    company_logo: "",
    certificate_logo: "",
  });

  // State to track whether each document type uses file or link
  const [documentInputTypes, setDocumentInputTypes] = useState({
    supplier_report: "file", // "file" or "link"
    lab_test: "file",
    certification: "file",
    reference: "file",
    company_logo: "file",
    certificate_logo: "file",
  });

  const [countryData, setCountryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTextarea, setActiveTextarea] = useState(null);

  const LABEL_OPTIONS = [
    "Plantbased",
    "Vegan",
    "Chemical focus",
    "Free Shipping",
    "SDG focus",
    "CO2 focus",
    "Water focus",
    "YFS discount",
    "Energy focus",
    "Reduced weight",
    "Plastic free",
    "C2C certified",
  ];

  useEffect(() => {
    // Load country data for shipping
    getTransportCountryData({
      dropdownData: countryData,
      setDropdownData: setCountryData,
    });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "same_contact") {
      // Handle the same contact checkbox
      setFormData((prevData) => {
        if (checked) {
          // Copy marketing contact info to review contact fields
          return {
            ...prevData,
            same_contact: checked,
            contact_person: prevData.marketing_contact_person,
            contact_phone: prevData.marketing_contact_phone,
            contact_email: prevData.marketing_contact_email,
          };
        } else {
          // Clear review contact fields when unchecked
          return {
            ...prevData,
            same_contact: checked,
            contact_person: "",
            contact_phone: "",
            contact_email: "",
          };
        }
      });
    } else {
      // Handle other form fields
      setFormData((prevData) => {
        const updatedData = {
          ...prevData,
          [name]: type === "checkbox" ? checked : value,
        };

        // If same_contact is checked and marketing contact fields are being updated,
        // also update the review contact fields
        if (
          prevData.same_contact &&
          (name === "marketing_contact_person" ||
            name === "marketing_contact_phone" ||
            name === "marketing_contact_email")
        ) {
          const fieldMapping = {
            marketing_contact_person: "contact_person",
            marketing_contact_phone: "contact_phone",
            marketing_contact_email: "contact_email",
          };

          if (fieldMapping[name]) {
            updatedData[fieldMapping[name]] =
              type === "checkbox" ? checked : value;
          }
        }

        return updatedData;
      });
    }
  };

  const handleSliderChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...featuredProducts];
    updatedProducts[index][field] = value;
    setFeaturedProducts(updatedProducts);
  };

  const handleProductImageChange = (index, file) => {
    const updatedProducts = [...featuredProducts];
    updatedProducts[index].image = file;
    setFeaturedProducts(updatedProducts);
  };

  // New: handle LCA calculation file upload for product
  const handleProductLcaFileChange = (index, file) => {
    const updatedProducts = [...featuredProducts];
    updatedProducts[index].lca_calculation = file;
    setFeaturedProducts(updatedProducts);
  };

  const addProduct = () => {
    setFeaturedProducts([
      ...featuredProducts,
      {
        product_name: "",
        product_description: "",
        image: null,
        price: "",
        unit_qty: "",
        unit: "",
        individual_store_price: false,
      },
    ]);
  };

  const removeProduct = (index) => {
    if (featuredProducts.length > 1) {
      setFeaturedProducts(featuredProducts.filter((_, i) => i !== index));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setDocuments({
      ...documents,
      [name]: files[0],
    });
  };

  // New handlers for document links and link texts
  const handleDocumentLinkChange = (documentType, value) => {
    setDocumentLinks({
      ...documentLinks,
      [documentType]: value,
    });
  };

  const handleDocumentLinkTextChange = (documentType, value) => {
    setDocumentLinkTexts({
      ...documentLinkTexts,
      [documentType]: value,
    });
  };

  // Handler to toggle between file and link input for documents
  const handleDocumentInputTypeChange = (documentType, inputType) => {
    setDocumentInputTypes({
      ...documentInputTypes,
      [documentType]: inputType,
    });

    // Clear the other input type when switching
    if (inputType === "file") {
      setDocumentLinks({
        ...documentLinks,
        [documentType]: "",
      });
      setDocumentLinkTexts({
        ...documentLinkTexts,
        [documentType]: "",
      });
    } else {
      setDocuments({
        ...documents,
        [documentType]: null,
      });
    }
  };

  const handleCountrySelection = (country) => {
    if (!country || country === "") return;

    setFormData((prevData) => {
      // Ensure shipping_countries is always an array
      const currentCountries = Array.isArray(prevData.shipping_countries)
        ? prevData.shipping_countries
        : [];

      const updatedCountries = currentCountries.includes(country)
        ? currentCountries.filter((c) => c !== country)
        : [...currentCountries, country];

      return {
        ...prevData,
        shipping_countries: updatedCountries,
      };
    });
  };

  const handleTextareaClick = (fieldName) => {
    setActiveTextarea(fieldName);
  };

  const validateImageFile = (file) => {
    if (!file) {
      return "No file provided";
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 1 * 1024 * 1024; // 1MB in bytes

    if (!allowedTypes.includes(file.type)) {
      console.log("File type not allowed");
      return "Only JPG, JPEG, and PNG files are allowed";
    }

    if (file.size > maxSize) {
      return "File size must be less than 1MB";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate images first
      for (let i = 0; i < featuredProducts.length; i++) {
        const product = featuredProducts[i];

        if (product.image) {
          const imageError = validateImageFile(product.image);

          if (imageError) {
            setError(`Product ${i + 1} image: ${imageError}`);
            setLoading(false);
            return;
          }
        } else {
          setError(`Product ${i + 1} has no image`);
          setLoading(false);
          return;
        }
      }

      // Validate required fields
      if (!formData.company_name.trim()) {
        setError("Company name is required");
        setLoading(false);
        return;
      }

      if (!formData.company_description.trim()) {
        setError("Company description is required");
        setLoading(false);
        return;
      }

      // Validate that at least one product has a name and description
      const hasValidProduct = featuredProducts.some(
        (product) =>
          product.product_name.trim() && product.product_description.trim()
      );

      if (!hasValidProduct) {
        setError("At least one product with name and description is required");
        setLoading(false);
        return;
      }

      const formDataToSend = {
        ...formData,
      };

      // Use the new combined endpoint
      const response =
        await partnershipService.createPartnershipWithDocumentsAndProducts(
          formDataToSend,
          documents,
          featuredProducts,
          documentLinks,
          documentLinkTexts,
          documentInputTypes
        );

      setSuccess(true);

      // Reset form
      setFormData({
        company_name: "",
        company_description: "",
        general_tagline: "",
        yfs_discount: false,
        yfs_discount_percentage: "",
        free_shipping: false,
        shipping_countries: [],
        social_impact: "",
        lca_impact: "",
        business_model: "",
        availability: "",
        business_size: "",
        marketing_contact_person: "",
        marketing_contact_phone: "",
        marketing_contact_email: "",
        contact_person: "",
        contact_phone: "",
        contact_email: "",
        same_contact: false,
        // Business details
        company_address_line1: "",
        company_address_line2: "",
        postal_code: "",
        city: "",
        country: "",
        vat_number: "",
        company_email: "",
        company_phone_number: "",
        // Label
        label: "",
      });

      setFeaturedProducts([
        {
          product_name: "",
          product_description: "",
          image: null,
          price: "",
          unit_qty: "",
          unit: "",
          individual_store_price: false,
        },
      ]);

      setDocuments({
        supplier_report: null,
        lab_test: null,
        certification: null,
        reference: null,
        company_logo: null,
        certificate_logo: null,
      });

      setDocumentLinks({
        supplier_report: "",
        lab_test: "",
        certification: "",
        reference: "",
        company_logo: "",
        certificate_logo: "",
      });

      setDocumentLinkTexts({
        supplier_report: "",
        lab_test: "",
        certification: "",
        reference: "",
        company_logo: "",
        certificate_logo: "",
      });

      setDocumentInputTypes({
        supplier_report: "file",
        lab_test: "file",
        certification: "file",
        reference: "file",
        company_logo: "file",
        certificate_logo: "file",
      });

      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 2000);
    } catch (err) {
      console.error("Error submitting partnership application:", err);

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to submit partnership application. Please try again.";

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const SliderToggle = ({ value, onChange, label }) => (
    <div className="partnership-slider-toggle">
      <span className="partnership-slider-label">{label}</span>
      <div className="partnership-slider-container">
        <button
          type="button"
          className={`partnership-slider-option ${!value ? "active" : ""}`}
          onClick={() => onChange(false)}
        >
          No
        </button>
        <button
          type="button"
          className={`partnership-slider-option ${value ? "active" : ""}`}
          onClick={() => onChange(true)}
        >
          Yes
        </button>
      </div>
    </div>
  );

  // Component for document input type toggle
  const DocumentInputToggle = ({ documentType, currentType, onChange }) => (
    <div className="partnership-document-toggle">
      <button
        type="button"
        className={`partnership-document-toggle-btn ${
          currentType === "file" ? "active" : ""
        }`}
        onClick={() => onChange(documentType, "file")}
      >
        Upload File
      </button>
      <button
        type="button"
        className={`partnership-document-toggle-btn ${
          currentType === "link" ? "active" : ""
        }`}
        onClick={() => onChange(documentType, "link")}
      >
        Add Link
      </button>
    </div>
  );

  return (
    <div className="partnership-modal-overlay">
      <div className="partnership-modal-container">
        <div className="partnership-modal-header">
          <h2>Partnership Application</h2>
          {onClose && (
            <button className="partnership-modal-close" onClick={onClose}>
              ×
            </button>
          )}
        </div>

        {success && (
          <div className="partnership-success-message">
            Your partnership application has been submitted successfully! Our
            team will review it shortly.
          </div>
        )}
        {error && <div className="partnership-error-message">{error}</div>}

        <div className="partnership-modal-content">
          <form className="partnership-form" onSubmit={handleSubmit}>
            {/* Header Section */}
            <div className="partnership-form-intro">
              <div className="ambition-intro-card-container">
                <h5 style={{ fontWeight: "bold" }}>Partnership</h5>
                <img
                  src={solutionIcon}
                  alt="Ambition Icon"
                  width={70}
                  height={70}
                  style={{ objectFit: "cover" }}
                />
              </div>

              <div
                className="ambition-desc-box"
                style={{ color: "#193666", fontSize: "15px" }}
              >
                <p>
                  Flash your most progressive products or services on the
                  Solution Lab.
                </p>
                <p>YFS evaluates all the factors below on a scale from 1-10.</p>
                <p>
                  Put in your pride products or services that are the top of
                  your performance. All added products/service has to comply to
                  the criterias you have noted.
                </p>
              </div>
            </div>

            {/* Business Details Section (grouped) */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>Business details</label>
              </div>
              <div className="partnership-form-input-col">
                {/* Company Name */}
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  placeholder="Company name"
                  style={{ marginBottom: "10px" }}
                />
                {/* Company Description */}
                {activeTextarea === "company_description" ||
                formData.company_description ? (
                  <textarea
                    type="text"
                    name="company_description"
                    value={formData.company_description}
                    onChange={handleChange}
                    placeholder="Describe your company and products (max 1500 characters)"
                    maxLength={1500}
                    style={{ marginBottom: "10px" }}
                  />
                ) : (
                  <div
                    className="partnership-form-placeholder"
                    onClick={() => handleTextareaClick("company_description")}
                    style={{ marginBottom: "10px" }}
                  >
                    Describe your company and products
                  </div>
                )}
                <small
                  style={{
                    display: "block",
                    marginBottom: "10px",
                    fontSize: "11px",
                  }}
                >
                  {formData.company_description.length}/1500 characters
                </small>
                {/* Company Email */}
                <input
                  type="email"
                  name="company_email"
                  value={formData.company_email}
                  onChange={handleChange}
                  placeholder="Company email"
                  style={{ marginBottom: "10px" }}
                />
                {/* Company Phone Number */}
                <input
                  type="text"
                  name="company_phone_number"
                  value={formData.company_phone_number}
                  onChange={handleChange}
                  placeholder="Company phone number"
                  style={{ marginBottom: "10px" }}
                />
                {/* Address Line 1 */}
                <input
                  type="text"
                  name="company_address_line1"
                  value={formData.company_address_line1}
                  onChange={handleChange}
                  placeholder="Company address line"
                  style={{ marginBottom: "10px" }}
                />
                {/* Address Line 2 */}
                <input
                  type="text"
                  name="company_address_line2"
                  value={formData.company_address_line2}
                  onChange={handleChange}
                  placeholder="Company address line 2"
                  style={{ marginBottom: "10px" }}
                />
                {/* Postal code and City */}
                <div
                  style={{ display: "flex", gap: "10px", marginBottom: "10px" }}
                >
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    placeholder="Postal code"
                  />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>
                {/* Country Dropdown */}
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "12px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                    fontSize: "12px",
                    color: "#0c376a",
                    backgroundColor: "white",
                  }}
                >
                  <option value="">Select Country</option>
                  {countryData.map((country) => (
                    <option key={country.id} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {/* VAT Number */}
                <input
                  type="text"
                  name="vat_number"
                  value={formData.vat_number}
                  onChange={handleChange}
                  placeholder="Vat number"
                />
              </div>
            </div>

            {/* Your Featured Products */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>Your Featured products</label>
              </div>
              <div className="partnership-form-input-col">
                {featuredProducts.map((product, index) => (
                  <div
                    key={index}
                    className="partnership-featured-product-item"
                  >
                    <div className="product-fields">
                      <input
                        type="text"
                        placeholder="Add product name +"
                        value={product.product_name}
                        onChange={(e) =>
                          handleProductChange(
                            index,
                            "product_name",
                            e.target.value
                          )
                        }
                        required
                      />

                      <div style={{ marginTop: "10px" }}>
                        {activeTextarea === `product_description_${index}` ||
                        product.product_description ? (
                          <textarea
                            placeholder="Add product description +"
                            value={product.product_description}
                            onChange={(e) =>
                              handleProductChange(
                                index,
                                "product_description",
                                e.target.value
                              )
                            }
                            required
                          />
                        ) : (
                          <div
                            className="partnership-form-placeholder"
                            onClick={() =>
                              handleTextareaClick(
                                `product_description_${index}`
                              )
                            }
                          >
                            Add product description +
                          </div>
                        )}
                      </div>

                      <div className="partnership-image-upload-section">
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          onChange={(e) =>
                            handleProductImageChange(index, e.target.files[0])
                          }
                          className="partnership-image-upload-input"
                        />
                        <small className="file-info">
                          Images max 1MB (JPG, JPEG, PNG only)
                        </small>
                        {product.image && (
                          <div className="partnership-image-preview">
                            <img
                              src={URL.createObjectURL(product.image)}
                              alt="Product preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* LCA Calculation File Upload */}
                      <div
                        className="partnership-lca-upload-section"
                        style={{ marginTop: "10px", fontSize: "12px" }}
                      >
                        <label
                          style={{
                            fontSize: "13px",
                            color: "#0c376a",
                            fontWeight: 500,
                          }}
                        >
                          Upload LCA / calculation
                        </label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.xlsx,.csv"
                          onChange={(e) =>
                            handleProductLcaFileChange(index, e.target.files[0])
                          }
                          className="partnership-lca-upload-input"
                        />
                        <small className="file-info">
                          PDF, JPG, PNG, Excel, CSV (max 1MB)
                        </small>
                        {product.lca_calculation && (
                          <div
                            className="partnership-lca-file-preview"
                            style={{
                              marginTop: "5px",
                              fontSize: "12px",
                              color: "#0c376a",
                            }}
                          >
                            {product.lca_calculation.name}
                          </div>
                        )}
                      </div>
                      {/* CO2e Value Input */}
                      <div
                        className="partnership-co2e-section"
                        style={{
                          marginTop: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <label
                          style={{
                            fontSize: "13px",
                            color: "#0c376a",
                            fontWeight: 500,
                          }}
                        >
                          Type CO₂e in "kg CO₂e/unit"
                        </label>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          placeholder="e.g. 1.23"
                          value={product.co2e}
                          onChange={(e) =>
                            handleProductChange(index, "co2e", e.target.value)
                          }
                          style={{ width: "120px", padding: "6px" }}
                        />
                        <span style={{ fontSize: "12px", color: "#0c376a" }}>
                          kg CO₂e/unit
                        </span>
                      </div>

                      {/* Price Information Row */}
                      <div
                        className="partnership-price-row"
                        style={{
                          marginTop: "10px",
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        {/* Individual Store Price Checkbox */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginRight: "15px",
                          }}
                        >
                          <input
                            type="checkbox"
                            id={`individual-store-price-${index}`}
                            checked={product.individual_store_price}
                            onChange={(e) =>
                              handleProductChange(
                                index,
                                "individual_store_price",
                                e.target.checked
                              )
                            }
                            style={{ margin: 0 }}
                          />
                          <label
                            htmlFor={`individual-store-price-${index}`}
                            style={{
                              fontSize: "12px",
                              color: "#0c376a",
                              cursor: "pointer",
                              margin: 0,
                            }}
                          >
                            Individual store price
                          </label>
                        </div>

                        {product.individual_store_price ? (
                          /* Show "individual store price" text when checked */
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              color: "#0c376a",
                              fontSize: "14px",
                              fontWeight: "500",
                            }}
                          >
                            <span>Individual store price</span>
                          </div>
                        ) : (
                          /* Show price inputs when not checked */
                          <>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              <input
                                type="number"
                                placeholder="Price"
                                value={product.price}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    "price",
                                    e.target.value
                                  )
                                }
                                style={{ width: "100px" }}
                                required={!product.individual_store_price}
                              />
                              <span style={{ fontSize: "12px" }}>kr</span>
                            </div>
                            <span>/</span>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              <input
                                type="number"
                                placeholder="Qty"
                                value={product.unit_qty}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    "unit_qty",
                                    e.target.value
                                  )
                                }
                                style={{ width: "80px" }}
                                required={!product.individual_store_price}
                              />
                              <input
                                type="text"
                                placeholder="Unit (e.g., pages, pieces)"
                                value={product.unit}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    "unit",
                                    e.target.value
                                  )
                                }
                                style={{ width: "200px", padding: "6px" }}
                                required={!product.individual_store_price}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {featuredProducts.length > 1 && (
                      <button
                        type="button"
                        className="partnership-remove-product-btn"
                        onClick={() => removeProduct(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="partnership-add-product-btn"
                  onClick={addProduct}
                >
                  + Add Another Product
                </button>
              </div>
            </div>

            {/* YFS Discount */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>YFS discount</label>
              </div>
              <div className="partnership-form-input-col">
                <SliderToggle
                  value={formData.yfs_discount}
                  onChange={(value) =>
                    handleSliderChange("yfs_discount", value)
                  }
                  label="Site wide ? (or no, featured products only )"
                />
                {formData.yfs_discount && (
                  <div className="discount-percentage">
                    <label>Add discount : </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.yfs_discount_percentage}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          yfs_discount_percentage: e.target.value,
                        })
                      }
                      placeholder="0"
                    />
                    <span>%</span>
                  </div>
                )}
              </div>
            </div>

            {/* Free Shipping */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>Free WW shipping:</label>
              </div>
              <div className="partnership-form-input-col">
                <SliderToggle
                  value={formData.free_shipping}
                  onChange={(value) =>
                    handleSliderChange("free_shipping", value)
                  }
                  label="Free shipping to: (Country dropdown +)"
                />
                {formData.free_shipping && (
                  <div className="partnership-country-selection">
                    <select
                      onChange={(e) => handleCountrySelection(e.target.value)}
                      value=""
                    >
                      <option value="">Select Country</option>
                      {countryData.map((country) => (
                        <option key={country.id} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <div className="partnership-selected-countries">
                      {formData.shipping_countries.map((country, index) => (
                        <span key={index} className="partnership-country-tag">
                          {country}
                          <button
                            type="button"
                            style={{ border: "none", marginBottom: "2px" }}
                            onClick={() => handleCountrySelection(country)}
                          >
                            <img
                              src={selectRemove}
                              alt="Remove icon"
                              width={14}
                              height={14}
                            />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* After Free Shipping, add Label Selector */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>Choose your label</label>
                <p className="partnership-form-sublabel">
                  You can choose only one key focus
                </p>
              </div>
              <div className="partnership-form-input-col">
                <div className="partnership-label-selector-grid">
                  {LABEL_OPTIONS.map((option) => (
                    <label
                      key={option}
                      className={`partnership-label-selector-option${
                        formData.label === option ? " selected" : ""
                      }`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        margin: "6px 10px 6px 0",
                        cursor: "pointer",
                        fontSize: "14px",
                        color: formData.label === option ? "#fff" : "#0c376a",
                        background:
                          formData.label === option ? "#0c376a" : "#f5f5f5",
                        borderRadius: "20px",
                        padding: "8px 18px",
                        border:
                          formData.label === option
                            ? "2px solid #0c376a"
                            : "1px solid #ddd",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="radio"
                        name="label"
                        value={option}
                        checked={formData.label === option}
                        onChange={handleChange}
                        style={{ display: "none" }}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* General Tagline */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>General tag line</label>
              </div>
              <div className="partnership-form-input-col">
                {activeTextarea === "general_tagline" ||
                formData.general_tagline ? (
                  <textarea
                    type="text"
                    name="general_tagline"
                    value={formData.general_tagline}
                    onChange={handleChange}
                    placeholder="Add a tagline describing the core values and differentiation of your products (max 25 characters)"
                    maxLength={25}
                  />
                ) : (
                  <div
                    className="partnership-form-placeholder"
                    onClick={() => handleTextareaClick("general_tagline")}
                  >
                    Add a tagline describing the core values and differentiation
                    of your products (max 25 characters)
                  </div>
                )}

                <small style={{ fontSize: "11px" }}>
                  {formData.general_tagline.length}/25 characters
                </small>
              </div>
            </div>

            {/* Impact LCA */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>Impact Ambitions</label>
              </div>
              <div className="partnership-form-input-col">
                {activeTextarea === "lca_impact" || formData.lca_impact ? (
                  <textarea
                    name="lca_impact"
                    value={formData.lca_impact}
                    onChange={handleChange}
                    placeholder="Describe what ambitions you have concerning impact - are you following the Planetary boundaries, if so how, if not why, and then what are your ambitions?"
                    required
                  />
                ) : (
                  <div
                    className="partnership-form-placeholder"
                    onClick={() => handleTextareaClick("lca_impact")}
                  >
                    Describe what ambitions you have concerning impact - are you
                    following the Planetary boundaries, if so how, if not why,
                    and then what are your ambitions?
                  </div>
                )}
              </div>
            </div>

            {/* Business Model */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>Business model</label>
              </div>
              <div className="partnership-form-input-col">
                {activeTextarea === "business_model" ||
                formData.business_model ? (
                  <textarea
                    name="business_model"
                    value={formData.business_model}
                    onChange={handleChange}
                    placeholder="Are you pushing products out you never see again or have you changed your company to take responsibility for your products with eg. take-back business model?"
                    required
                  />
                ) : (
                  <div
                    className="partnership-form-placeholder"
                    onClick={() => handleTextareaClick("business_model")}
                  >
                    Are you pushing products out you never see again or have you
                    changed your company to take responsibility for your
                    products with eg. take-back business model?
                  </div>
                )}
              </div>
            </div>

            {/* Product Core Values */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>Product core values</label>
              </div>
              <div className="partnership-form-input-col">
                {activeTextarea === "social_impact" ||
                formData.social_impact ? (
                  <textarea
                    name="social_impact"
                    value={formData.social_impact}
                    onChange={handleChange}
                    placeholder="Are you changing social culture or promoting a healthier behaviour directly with your product?"
                    required
                  />
                ) : (
                  <div
                    className="partnership-form-placeholder"
                    onClick={() => handleTextareaClick("social_impact")}
                  >
                    Are you changing social culture or promoting a healthier
                    behaviour directly with your product?
                  </div>
                )}
              </div>
            </div>

            {/* Readily Available */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>Readily available</label>
              </div>
              <div className="partnership-form-input-col">
                {activeTextarea === "availability" || formData.availability ? (
                  <textarea
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    placeholder="Is the product available globally or just in some locations?"
                    required
                  />
                ) : (
                  <div
                    className="partnership-form-placeholder"
                    onClick={() => handleTextareaClick("availability")}
                  >
                    Is the product available globally or just in some locations?
                  </div>
                )}
              </div>
            </div>

            {/* Documentation Section */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>Documentation</label>
                <p className="partnership-form-sublabel">
                  Upload Logo, references and documentation of your claims:
                  Certificates, supplier reports, lab tests etc.
                </p>
                <p className="partnership-form-sublabel">
                  You can either upload files (max 1MB) or provide links to
                  external documents.
                </p>
              </div>
              <div className="partnership-form-input-col">
                <div className="partnership-form-file-grid">
                  {/* Supplier Report */}
                  <div className="partnership-form-file-item">
                    <label>Supplier Report</label>
                    <DocumentInputToggle
                      documentType="supplier_report"
                      currentType={documentInputTypes.supplier_report}
                      onChange={handleDocumentInputTypeChange}
                    />
                    {documentInputTypes.supplier_report === "file" ? (
                      <input
                        type="file"
                        name="supplier_report"
                        accept=".pdf,.jpg,.png"
                        onChange={handleFileChange}
                      />
                    ) : (
                      <div className="partnership-link-inputs">
                        <input
                          type="text"
                          placeholder="Link text (e.g., 'naturli | CarbonCloud')"
                          value={documentLinkTexts.supplier_report}
                          onChange={(e) =>
                            handleDocumentLinkTextChange(
                              "supplier_report",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="url"
                          placeholder="Document URL"
                          value={documentLinks.supplier_report}
                          onChange={(e) =>
                            handleDocumentLinkChange(
                              "supplier_report",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Lab Test */}
                  <div className="partnership-form-file-item">
                    <label>Lab Test</label>
                    <DocumentInputToggle
                      documentType="lab_test"
                      currentType={documentInputTypes.lab_test}
                      onChange={handleDocumentInputTypeChange}
                    />
                    {documentInputTypes.lab_test === "file" ? (
                      <input
                        type="file"
                        name="lab_test"
                        accept=".pdf,.jpg,.png"
                        onChange={handleFileChange}
                      />
                    ) : (
                      <div className="partnership-link-inputs">
                        <input
                          type="text"
                          placeholder="Link text (e.g., 'naturli | CarbonCloud')"
                          value={documentLinkTexts.lab_test}
                          onChange={(e) =>
                            handleDocumentLinkTextChange(
                              "lab_test",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="url"
                          placeholder="Document URL"
                          value={documentLinks.lab_test}
                          onChange={(e) =>
                            handleDocumentLinkChange("lab_test", e.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Certification */}
                  <div className="partnership-form-file-item">
                    <label>Certification</label>
                    <DocumentInputToggle
                      documentType="certification"
                      currentType={documentInputTypes.certification}
                      onChange={handleDocumentInputTypeChange}
                    />
                    {documentInputTypes.certification === "file" ? (
                      <input
                        type="file"
                        name="certification"
                        accept=".pdf,.jpg,.png"
                        onChange={handleFileChange}
                      />
                    ) : (
                      <div className="partnership-link-inputs">
                        <input
                          type="text"
                          placeholder="Link text (e.g., 'naturli | CarbonCloud')"
                          value={documentLinkTexts.certification}
                          onChange={(e) =>
                            handleDocumentLinkTextChange(
                              "certification",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="url"
                          placeholder="Document URL"
                          value={documentLinks.certification}
                          onChange={(e) =>
                            handleDocumentLinkChange(
                              "certification",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Reference */}
                  <div className="partnership-form-file-item">
                    <label>Reference</label>
                    <DocumentInputToggle
                      documentType="reference"
                      currentType={documentInputTypes.reference}
                      onChange={handleDocumentInputTypeChange}
                    />
                    {documentInputTypes.reference === "file" ? (
                      <input
                        type="file"
                        name="reference"
                        accept=".pdf,.jpg,.png"
                        onChange={handleFileChange}
                      />
                    ) : (
                      <div className="partnership-link-inputs">
                        <input
                          type="text"
                          placeholder="Link text (e.g., 'naturli | CarbonCloud')"
                          value={documentLinkTexts.reference}
                          onChange={(e) =>
                            handleDocumentLinkTextChange(
                              "reference",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="url"
                          placeholder="Document URL"
                          value={documentLinks.reference}
                          onChange={(e) =>
                            handleDocumentLinkChange(
                              "reference",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Company Logo */}
                  <div className="partnership-form-file-item">
                    <label>Company Logo</label>
                    <DocumentInputToggle
                      documentType="company_logo"
                      currentType={documentInputTypes.company_logo}
                      onChange={handleDocumentInputTypeChange}
                    />
                    {documentInputTypes.company_logo === "file" ? (
                      <input
                        type="file"
                        name="company_logo"
                        accept=".jpg,.png"
                        onChange={handleFileChange}
                      />
                    ) : (
                      <div className="partnership-link-inputs">
                        <input
                          type="text"
                          placeholder="Link text (e.g., 'naturli | CarbonCloud')"
                          value={documentLinkTexts.company_logo}
                          onChange={(e) =>
                            handleDocumentLinkTextChange(
                              "company_logo",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="url"
                          placeholder="Document URL"
                          value={documentLinks.company_logo}
                          onChange={(e) =>
                            handleDocumentLinkChange(
                              "company_logo",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Certificate Logo */}
                  <div className="partnership-form-file-item">
                    <label>Certificate Logo</label>
                    <DocumentInputToggle
                      documentType="certificate_logo"
                      currentType={documentInputTypes.certificate_logo}
                      onChange={handleDocumentInputTypeChange}
                    />
                    {documentInputTypes.certificate_logo === "file" ? (
                      <input
                        type="file"
                        name="certificate_logo"
                        accept=".jpg,.png"
                        onChange={handleFileChange}
                      />
                    ) : (
                      <div className="partnership-link-inputs">
                        <input
                          type="text"
                          placeholder="Link text (e.g., 'naturli | CarbonCloud')"
                          value={documentLinkTexts.certificate_logo}
                          onChange={(e) =>
                            handleDocumentLinkTextChange(
                              "certificate_logo",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="url"
                          placeholder="Document URL"
                          value={documentLinks.certificate_logo}
                          onChange={(e) =>
                            handleDocumentLinkChange(
                              "certificate_logo",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Business Size */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>What is your business type?</label>
              </div>
              <div className="partnership-form-input-col">
                <select
                  name="business_size"
                  value={formData.business_size}
                  onChange={handleChange}
                  required
                >
                  <option value="">Business-size</option>
                  <option value="Start-up / Micro">Start-up / Micro</option>
                  <option value="Small (10-20)">Small (10-20)</option>
                  <option value="Medium (21-150)">Medium (21-150)</option>
                  <option value="Large (151-1000)">Large (151-1000)</option>
                  <option value="Corporate (1001+)">Corporate (1001+)</option>
                  <option value="NGO">NGO</option>
                </select>
              </div>
            </div>

            {/* Marketing Contact Information */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>
                  Who should YFS users contact if interested in your product?
                </label>
              </div>
              <div className="partnership-form-input-col">
                <div className="partnership-form-contact-grid">
                  <input
                    type="text"
                    name="marketing_contact_person"
                    value={formData.marketing_contact_person}
                    onChange={handleChange}
                    placeholder="Marketing contact person"
                    required
                  />
                  <input
                    type="text"
                    name="marketing_contact_phone"
                    value={formData.marketing_contact_phone}
                    onChange={handleChange}
                    placeholder="Marketing contact phone (incl. country code)"
                    required
                  />
                  <input
                    type="email"
                    name="marketing_contact_email"
                    value={formData.marketing_contact_email}
                    onChange={handleChange}
                    placeholder="Marketing contact mail"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="partnership-form-row">
              <div className="partnership-form-label-col">
                <label>
                  Who is your contact for us to review your application?
                </label>
              </div>
              <div className="partnership-form-input-col">
                <div className="partnership-form-contact-grid">
                  {formData.same_contact && (
                    <small
                      style={{
                        color: "#666",
                        fontStyle: "italic",
                        marginTop: "5px",
                      }}
                    >
                      Using the same contact information as marketing contact
                    </small>
                  )}

                  {/* Same Contact Checkbox */}
                  <div className="partnership-form-row">
                    <div className="partnership-form-label-col">
                      <label>
                        Is the marketing contact the same as the review contact?
                      </label>
                    </div>
                    <div
                      className="partnership-form-input-col"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <input
                        type="checkbox"
                        name="same_contact"
                        checked={formData.same_contact}
                        onChange={handleChange}
                        style={{ marginRight: "10px" }}
                      />
                      <label>Yes, they are the same person.</label>
                    </div>
                  </div>
                  <input
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleChange}
                    placeholder="Contact person"
                    required={!formData.same_contact}
                    disabled={formData.same_contact}
                    style={{
                      opacity: formData.same_contact ? 0.6 : 1,
                      backgroundColor: formData.same_contact
                        ? "#f5f5f5"
                        : "white",
                    }}
                  />
                  <input
                    type="text"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="Contact phone (incl. country code)"
                    required={!formData.same_contact}
                    disabled={formData.same_contact}
                    style={{
                      opacity: formData.same_contact ? 0.6 : 1,
                      backgroundColor: formData.same_contact
                        ? "#f5f5f5"
                        : "white",
                    }}
                  />
                  <input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="Contact mail"
                    required={!formData.same_contact}
                    disabled={formData.same_contact}
                    style={{
                      opacity: formData.same_contact ? 0.6 : 1,
                      backgroundColor: formData.same_contact
                        ? "#f5f5f5"
                        : "white",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="partnership-form-actions">
              <button
                type="submit"
                className="partnership-submit-button"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Send application"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PartnershipForm;
